import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Button, Avatar, IconButton, Tooltip, Pagination, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery
} from "@mui/material";
import { getAuthToken } from "../../utils/getAuthToken";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import RestoreIcon from '@mui/icons-material/Restore';
import dayjs from "dayjs";
import axios from "axios";
import VersionViewer from "./VersionViewer";
import { useDispatch } from "react-redux";
import { fetchWorkflowById } from "../../redux/Slice/Workflow/workflowSlice";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const formatDate = (date) => dayjs(date).format("D MMM YY [at] h:mma");

const PAGE_SIZE = 10;

const RevisionsTab = ({ workflowId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const chipBg = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)";
  const iconColor = isDark ? "#fff" : undefined;
  const dispatch = useDispatch();

  const [versions, setVersions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalVersions, setTotalVersions] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [reverting, setReverting] = useState(false);

  const fetchVersions = useCallback(async (page = 1) => {
    if (!workflowId) return;
    setLoading(true);
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${SERVER_DOMAIN}/api/workflow/getversions/${workflowId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          page: page,
          limit: PAGE_SIZE
        }
      });
      
      const allVersions = [response.data.currentVersion, ...(response.data.versions || [])];
      setVersions(allVersions);
      setTotalPages(response.data.pagination?.totalPages || 0);
      setTotalVersions(response.data.pagination?.totalVersions || 0);
    } catch (error) {
      console.error("Error fetching versions:", error);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    fetchVersions(currentPage);
  }, [workflowId, currentPage, fetchVersions]);

  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedVersion(null);
  };

  const handleRevert = async () => {
    if (!selectedVersion || !workflowId) return;
    
    setReverting(true);
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Prepare the update data with the selected version as current version
      const updateData = {
        currentVersion: {
          name: selectedVersion.name,
          description: selectedVersion.description,
          branches: selectedVersion.branches || { start: [] },
        },
        status: "off" // Keep the workflow off when reverting
      };

      // Call the update API
      await axios.put(
        `${SERVER_DOMAIN}/api/workflow/update/${workflowId}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Refresh the workflow data in Redux
      await dispatch(fetchWorkflowById(workflowId));
      
      // Refresh the versions list
      await fetchVersions(currentPage);

      // Close the dialog
      handleCloseViewDialog();
      
    } catch (error) {
      console.error("Error reverting workflow:", error);
      // You might want to show an error message to the user here
    } finally {
      setReverting(false);
    }
  };

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  return (
    <Box sx={{ 
      p: { xs: 0.75, sm: 1, md: 2, lg: 2.5, xl: 3 }, 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      flex: { xs: 1, sm: 1 },
      minHeight: { xs: 0, sm: 0 },
      overflow: 'hidden',
    }}>
      <TableContainer 
        component={Paper} 
        sx={{ 
          flex: 1, 
          overflow: 'auto',
          maxHeight: { xs: 'none', sm: 'none', md: 'calc(100vh - 200px)', lg: 'calc(100vh - 200px)', xl: 'calc(100vh - 200px)' },
          minHeight: { xs: 0, sm: 0, md: '400px', lg: '400px', xl: '400px' },
          scrollbarWidth: { xs: 'none', sm: 'none', md: 'thin', lg: 'thin', xl: 'thin' },
          msOverflowStyle: { xs: 'none', sm: 'none', md: 'auto', lg: 'auto', xl: 'auto' },
          '&::-webkit-scrollbar': {
            width: { xs: 0, sm: 0, md: '8px', lg: '10px', xl: '10px' },
            height: { xs: 0, sm: 0, md: '8px', lg: '10px', xl: '10px' },
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Created on
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Revision
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Change
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}>
                Author
              </TableCell>
              <TableCell sx={{ 
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' },
                fontWeight: 'bold'
              }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {versions.map((ver, idx) => (
              <TableRow key={idx}>
                <TableCell sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '2px 4px', sm: '4px 6px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  {formatDate(ver.createdAt)}
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  <Chip 
                    label={`V${totalVersions - ((currentPage - 1) * PAGE_SIZE + idx)}`} 
                    size="small" 
                    sx={{ 
                      fontWeight: 600, 
                      bgcolor: chipBg, 
                      color: isDark ? '#fff' : undefined,
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
                      height: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '28px' }
                    }} 
                  />
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: {
                    xs: '10px 8px',
                    sm: '8px 10px',
                    md: '10px 12px',
                    lg: '12px 16px',
                    xl: '16px',
                  },
                  verticalAlign: 'middle',
                }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'flex-start',
                      gap: { xs: 1, sm: 0.75 },
                      flexWrap: 'wrap',
                    }}
                  >
                    <Chip 
                      icon={<AddIcon sx={{ color: iconColor, fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }} />} 
                      label={"1"} 
                      size="small" 
                      sx={{ 
                        bgcolor: chipBg, 
                        color: isDark ? '#fff' : undefined, 
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
                        height: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '28px' }
                      }} 
                    />
                    <Chip 
                      icon={<EditIcon sx={{ color: iconColor, fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }} />} 
                      label={"1"} 
                      size="small" 
                      sx={{ 
                        bgcolor: chipBg, 
                        color: isDark ? '#fff' : undefined, 
                        fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' },
                        height: { xs: '20px', sm: '22px', md: '24px', lg: '26px', xl: '28px' }
                      }} 
                    />
                  </Box>
                  {/* <Chip icon={<RemoveIcon sx={{ color: iconColor }} />} label={"1"} size="small" sx={{ bgcolor: chipBg, color: isDark ? '#fff' : undefined }} /> */}
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ 
                      width: { xs: 20, sm: 24, md: 26, lg: 28, xl: 28 }, 
                      height: { xs: 20, sm: 24, md: 26, lg: 28, xl: 28 }, 
                      fontSize: { xs: 10, sm: 12, md: 13, lg: 14, xl: 14 } 
                    }}>
                      {ver.createdBy?.name?.[0] || 'U'}
                    </Avatar>
                    <Box>
                      <Typography 
                        fontWeight={500} 
                        sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
                      >
                        {ver.createdBy?.name || 'Unknown'}
                      </Typography>
                      {!isXs && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem', lg: '0.8rem', xl: '0.875rem' } }}
                        >
                          {ver.createdBy?.email || ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </TableCell>
                <TableCell sx={{ 
                  fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' },
                  padding: { xs: '4px 8px', sm: '8px 10px', md: '10px 12px', lg: '12px 16px', xl: '16px' }
                }}>
                  <Tooltip title="View revision">
                    <Button 
                      size={isXs || isSm ? 'small' : 'medium'}
                      startIcon={<VisibilityIcon sx={{ color: iconColor, fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }} />} 
                      variant="text" 
                      sx={{ 
                        color: isDark ? '#fff' : '#888',
                        fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.9rem' },
                        px: { xs: 0.5, sm: 1, md: 1, lg: 1.5, xl: 1.5 },
                        py: { xs: 0.25, sm: 0.5, md: 0.5, lg: 0.625, xl: 0.625 }
                      }}
                      onClick={() => handleViewVersion(ver)}
                    >
                      View
                    </Button>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {versions.length === 0 && !loading && (
              <TableRow>
                <TableCell 
                  colSpan={5} 
                  align="center"
                  sx={{ 
                    padding: { xs: '16px 8px', sm: '20px 10px', md: '24px 12px', lg: '28px 16px', xl: '32px 16px' }
                  }}
                >
                  <Typography 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '1rem' } }}
                  >
                    No revisions found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row',
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: { xs: 0.5, sm: 0 },
        mt: { xs: 1, sm: 1.25, md: 2, lg: 2, xl: 2 }, 
        px: { xs: 0, sm: 1, md: 1.5, lg: 2, xl: 2 },
        flexShrink: 0,
        minWidth: 0,
      }}>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' },
            minWidth: 0,
            flexShrink: 1,
            ...(isXs && {
              pr: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
          }}
        >
          {versions.length > 0 && (
            <>
              {`${(currentPage - 1) * PAGE_SIZE + 1}-${(currentPage - 1) * PAGE_SIZE + versions.length}`} of {totalVersions} items
            </>
          )}
        </Typography>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          size={isXs || isSm ? 'small' : 'medium'}
          sx={{
            flexShrink: 0,
            '& .MuiPaginationItem-root': {
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.9rem' }
            }
          }}
        />
      </Box>

      {/* Version Viewer Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth={isXs || isSm ? 'md' : 'xl'}
        fullWidth={isXs || isSm}
        PaperProps={{
          sx: {
            /** xs/sm: fixed viewport height so VersionViewer/React Flow (height:100%) gets a real box — auto height collapsed body */
            display: 'flex',
            flexDirection: 'column',
            height: {
              xs: 'calc(100dvh - 24px)',
              sm: 'calc(100dvh - 24px)',
              md: '90vh',
              lg: '90vh',
              xl: '90vh',
            },
            maxHeight: {
              xs: 'calc(100dvh - 24px)',
              sm: 'calc(100dvh - 24px)',
              md: '90vh',
              lg: '90vh',
              xl: '90vh',
            },
          },
        }}
      >
        <DialogTitle 
          component="div"
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
            p: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 }
          }}
        >
          <Box>
            <Typography 
              variant={isXs || isSm ? 'subtitle1' : 'h6'} 
              component="span"
              sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem', lg: '1.125rem', xl: '1.25rem' } }}
            >
              {selectedVersion?.name || 'Workflow Version'}
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
            >
              Created on {selectedVersion ? formatDate(selectedVersion.createdAt) : ''}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            size={isXs || isSm ? 'small' : 'medium'}
            sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: { xs: 1, sm: 1.5, md: 2, lg: 2, xl: 2 },
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedVersion && (
            <VersionViewer version={selectedVersion} />
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 1.5, sm: 2, md: 2, lg: 2, xl: 2 }, 
          flexShrink: 0,
          borderTop: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem', lg: '0.875rem', xl: '0.875rem' } }}
          >
            This will replace the current workflow with this version
          </Typography>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1, md: 1, lg: 1, xl: 1 } }}>
            <Button 
              onClick={handleCloseViewDialog}
              variant="outlined"
              disabled={reverting}
              size={isXs || isSm ? 'small' : 'medium'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem', xl: '0.9rem' } }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleRevert}
              variant="contained"
              color="primary"
              startIcon={<RestoreIcon sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '0.9375rem', lg: '1rem', xl: '1rem' } }} />}
              disabled={reverting}
              size={isXs || isSm ? 'small' : 'medium'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem', lg: '0.9rem', xl: '0.9rem' } }}
            >
              {reverting ? 'Reverting...' : 'Revert to This Version'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RevisionsTab; 